/**
 * Component-level tokens — map to semantic tokens only (no raw hex in consumers).
 */

import { semantic, lightLiteral } from './semanticTokens';

const { fontFamily, transition } = semantic;

/**
 * The two colour values a component token owns outright (no semantic token to point at).
 * Light values here; `themePalettes.js` carries both palettes under `--cvx-component-<key>`.
 */
export const COMPONENT_COLORS_LIGHT = {
  /** Hairline around a dashboard card. */
  cardEdge: 'rgba(15, 23, 42, 0.06)',
  /**
   * Modal/drawer scrim. 0.65 (was 0.45) — at 45% the underlying page was readable through the
   * scrim on busy admin surfaces, undermining the "modal is focused" feel. 0.65 still feels
   * semi-transparent (you sense context behind) without letting underlying text/data leak through.
   */
  scrim: 'rgba(15, 23, 42, 0.65)',
  /** Light dim behind a menu portalled to <body> (bodyDimLayer). */
  scrimSoft: 'rgba(15, 23, 42, 0.35)',
  /** Dim behind the phone push-drawer. */
  scrimMenu: 'rgba(15, 23, 42, 0.28)',
  /** The ink an alpha-driven scrim is mixed from (`alphaOf(scrimInk, a)`). */
  scrimInk: '#0f172a',
};

export const componentVarName = (key) => `--cvx-component-${key}`;
const componentVars = Object.fromEntries(
  Object.keys(COMPONENT_COLORS_LIGHT).map((key) => [key, `var(${componentVarName(key)})`]),
);

/** Spacing / radius / type — structural */
export const structure = {
  radii: {
    sm: '8px',
    md: '10px',
    lg: '14px',
    xl: '18px',
    '2xl': '22px',
    full: '9999px',
  },
  space: {
    0: 0,
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 20,
    6: 24,
    7: 28,
    8: 32,
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.8125rem',
    base: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    /** Large KPI / hero numerals (dashboard tiles) */
    '3xl': '2rem',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
};

/**
 * Text size of every typing field on a TOUCH screen (KAN-969): phones, upright and sideways
 * iPads, touch laptops. 14px since KAN-1105 (16px read larger than the 13px label above it); iOS
 * Safari's focus zoom under 16px is off app-wide via `maximum-scale=1` in public/index.html. A computer with a mouse keeps the field's own size (13px).
 *
 * ONE global rule in `src/ui/fieldTouchFont.css` applies it (`--cvx-field-touch-font-size`, mirrored
 * there — keep them in step; `fieldTouchFontSize.guard.test.js` pins the pair). Fields inline their
 * 13px, so the rule is `!important`; a field whose box is not a typing field's (the transparent
 * one-time-code input) opts out with `data-cvx-field-text="fixed"`.
 */
export const FIELD_TOUCH_FONT_PX = 14;

/**
 * "A finger is the pointer" as CSS — exactly `isTouchLayout` (adminLayoutMode.js): a coarse
 * primary pointer OR a viewport under 1024. 1023.98 is "under 1024" for fractional widths.
 * The same query is written in `src/ui/fieldTouchFont.css`; keep them in step.
 */
export const FIELD_TOUCH_MEDIA_QUERY = '(pointer: coarse), (max-width: 1023.98px)';

export const spacePx = Object.fromEntries(
  Object.entries(structure.space).map(([k, v]) => [k, `${v}px`]),
);

/** Builds the component tokens from a semantic set + the component colours (var form or Light literal). */
const buildComponents = ({ bg, fg, border, brand, elevation }, cc) => ({
  shell: {
    appBackground: bg.canvas,
    railBackground: bg.surface,
    railBorder: border.default,
    headerBackground: bg.surface,
    headerBorder: border.default,
    mainMaxWidth: '1400px',
    railWidthExpanded: 248,
    railWidthCollapsed: 68,
  },
  button: {
    primary: {
      background: brand.primary,
      backgroundHover: brand.primaryHover,
      color: brand.onPrimary,
      border: `1px solid ${brand.primary}`,
      shadow: elevation.xs,
    },
    secondary: {
      background: bg.surface,
      color: brand.text,
      border: `1px solid ${border.strong}`,
      shadow: elevation.none,
    },
    ghost: {
      background: 'transparent',
      color: brand.text,
      border: '1px solid transparent',
    },
  },
  card: {
    background: bg.surface,
    border: `1px solid ${cc.cardEdge}`,
    shadow: elevation.widget,
    radius: structure.radii.lg,
    hoverShadow: elevation.widgetHover,
    hoverTransform: 'translateY(-2px)',
    transition: transition.normal,
  },
  input: {
    background: bg.surface,
    border: `1px solid ${border.field}`,
    color: fg.default,
    placeholder: fg.placeholder,
    focusRing: elevation.focusRing,
    focusOutline: elevation.fieldFocusRing,
    focusBorder: brand.primary,
    transition: transition.fast,
  },
  table: {
    rowHoverBg: bg.surfaceMuted,
    rowTransition: transition.fast,
  },
  tab: {
    inactive: { color: fg.muted, border: '1px solid transparent', background: 'transparent' },
    active: {
      color: brand.text,
      border: `1px solid ${brand.primary}`,
      background: bg.surfaceMuted,
    },
  },
  overlay: {
    scrim: cc.scrim,
    scrimSoft: cc.scrimSoft,
    scrimMenu: cc.scrimMenu,
    scrimInk: cc.scrimInk,
  },
  modal: {
    background: bg.surfaceElevated,
    border: `1px solid ${border.default}`,
    shadow: elevation.md,
    radius: structure.radii.xl,
  },
  drawer: {
    background: bg.surface,
    border: `1px solid ${border.default}`,
    shadow: elevation.md,
  },
  chip: {
    background: bg.surfaceMuted,
    border: `1px solid ${border.default}`,
    color: fg.secondary,
  },
  badge: {
    background: brand.primaryMuted,
    color: brand.text,
    border: `1px solid ${border.subtle}`,
  },
  /**
   * THE canonical pill — one recipe for every status AND label pill in the app
   * (invoice / estimate / job / shift / route / lifecycle / catalog / role /
   * platform chips) AND the legend chips that document them, so a pill and its
   * legend entry are the same rendered element and cannot drift (2026-08-06,
   * pill–legend unification). Colour stays with each domain's own token map —
   * that is what makes one status distinguishable from another; borders come
   * from the family's existing *Border token (neutral families use
   * color.neutral.border), never invented hex.
   *
   * Icon policy lives with consumers: a family whose LEGEND teaches a glyph
   * renders icon + word (the token's own Icon/iconSize/strokeWidth, gap 5px);
   * legend-less label families (role, plan, service type, seat, industry,
   * lifecycle) render the word alone. Geometry never varies between the two.
   */
  statusBadge: {
    fontFamily: fontFamily.sans,
    fontSize: structure.fontSize.xs,
    fontWeight: structure.fontWeight.semibold,
    letterSpacing: '0',
    lineHeight: 1.2,
    // 10px side gutter is inherited from the legend chip this recipe unifies
    // with — deliberately between spacePx[2] and [3]; this token is the ONE
    // place the literal may live.
    padding: '4px 10px',
    borderRadius: structure.radii.sm,
  },
  /** Light left navigation rail (desktop) */
  navRail: {
    background: bg.surfaceMuted,
    borderRight: `1px solid ${border.default}`,
    shadow: elevation.none,
    itemInactive: { color: fg.secondary, background: 'transparent' },
    itemHover: { color: fg.default, background: bg.surface },
    itemActive: {
      color: brand.text,
      background: brand.primaryMuted,
      border: '1px solid transparent',
    },
    iconBg: 'transparent',
    iconBgActive: 'transparent',
  },
  mobileDocument: {
    card: {
      padding: `${spacePx[3]} ${spacePx[4]}`,
      borderRadius: structure.radii.lg,
      shadow: elevation.xs,
      gap: spacePx[3],
      background: bg.surface,
      border: `1px solid ${border.default}`,
    },
    actionButton: {
      minHeight: '44px',
      fontSize: structure.fontSize.xs,
      padding: `${spacePx[2]} ${spacePx[3]}`,
      gap: spacePx[2],
    },
    filterBar: {
      height: '40px',
      gap: spacePx[2],
      scrollSnapType: 'x mandatory',
      scrollSnapAlign: 'start',
      WebkitOverflowScrolling: 'touch',
    },
  },
});

/** Theme-aware: colour values are `var(--cvx-…)` strings. This is what UI consumes. */
export const components = buildComponents(semantic, componentVars);

/** The fixed Light literals in the same shape — for consumers that need real colours. */
export const componentsLight = buildComponents(lightLiteral, COMPONENT_COLORS_LIGHT);

export const componentStructure = {
  structure,
  spacePx,
  fontFamily,
};
